import React, { useState, useEffect } from "react";
import { Menu, ShoppingBag, X, User as UserIcon, LogOut, Globe, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { useUserAuth } from "../context/UserAuthContext";
import { SearchModal } from "./SearchModal";

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user, logout } = useUserAuth();
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Keyboard shortcut: "/" focuses the global search (like GitHub / X)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !searchOpen) {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const links = [
    { href: "/#categories", label: t("nav.categories"), key: "categories" },
    { href: "/#bestsellers", label: t("nav.bestsellers"), key: "bestsellers" },
    { href: "/shop", label: t("nav.shop"), key: "shop" },
    { href: "/#about", label: t("nav.about"), key: "about" },
    { href: "/#store", label: t("nav.store"), key: "store" },
    { href: "/#newsletter", label: t("nav.club"), key: "club" },
  ];

  const toggleLang = () => {
    const next = i18n.language === "it" ? "en" : "it";
    i18n.changeLanguage(next);
  };

  return (
    <header data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-zinc-950/95 backdrop-blur-md border-b-2 border-yellow-400" : "bg-transparent"
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src="/brand/logo-128.png"
            alt="Daita 'n' Jack"
            className="w-12 h-12 object-contain drop-shadow-[0_0_8px_rgba(250,204,21,0.25)] hover:drop-shadow-[0_0_12px_rgba(250,204,21,0.55)] transition"
          />
          <div className="leading-none">
            <div className="font-heading text-2xl text-white uppercase tracking-tight">
              DAITA<span className="text-yellow-400">'N'</span>JACK
            </div>
            <div className="font-jp text-[10px] text-zinc-400 tracking-widest">大太とジャック</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <a key={l.key} href={l.href} data-testid={`nav-${l.key}`}
              className="text-xs uppercase tracking-widest text-zinc-300 hover:text-yellow-400 transition font-semibold">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="navbar-search-toggle"
            onClick={() => setSearchOpen(true)}
            aria-label={t("search.open")}
            className="flex items-center gap-1 border-2 border-zinc-800 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 p-2 sm:px-3 sm:py-2 transition"
          >
            <Search className="w-4 h-4" strokeWidth={3} />
            <span className="hidden xl:inline font-heading tracking-widest text-xs uppercase">{t("search.button")}</span>
            <kbd className="hidden xl:inline ml-1 px-1.5 py-0.5 border border-zinc-700 text-[10px] font-mono text-zinc-500 leading-none">/</kbd>
          </button>

          <button data-testid="lang-toggle" onClick={toggleLang}
            className="hidden sm:flex items-center gap-1 border-2 border-zinc-800 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 px-3 py-2 transition font-heading tracking-widest text-xs uppercase">
            <Globe className="w-4 h-4" strokeWidth={3} />
            {i18n.language === "it" ? "IT" : "EN"}
          </button>

          {user ? (
            <Link to="/account" data-testid="account-link"
              className="hidden md:flex items-center gap-2 border-2 border-zinc-800 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 px-3 py-2 transition font-heading tracking-widest text-xs uppercase">
              <UserIcon className="w-4 h-4" strokeWidth={3} /> {t("nav.account")}
            </Link>
          ) : (
            <Link to="/login" data-testid="login-link"
              className="hidden md:flex items-center gap-2 border-2 border-zinc-800 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 px-3 py-2 transition font-heading tracking-widest text-xs uppercase">
              <UserIcon className="w-4 h-4" strokeWidth={3} /> {t("nav.login")}
            </Link>
          )}

          <button data-testid="cart-button" onClick={() => setOpen(true)}
            className="relative bg-yellow-400 text-black p-3 hover:bg-white transition" aria-label="cart">
            <ShoppingBag className="w-5 h-5" strokeWidth={3} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-black text-yellow-400 text-xs font-bold w-6 h-6 flex items-center justify-center border-2 border-yellow-400">
                {count}
              </span>
            )}
          </button>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-white p-2" onClick={() => setMobile(!mobile)} aria-label="Menu">
            {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden bg-zinc-950 border-t-2 border-yellow-400 px-6 py-4 flex flex-col gap-4">
          <button
            data-testid="mobile-search-toggle"
            onClick={() => { setSearchOpen(true); setMobile(false); }}
            className="text-yellow-400 uppercase tracking-widest font-semibold flex items-center gap-2 text-left"
          >
            <Search className="w-4 h-4" /> {t("search.button")}
          </button>
          {links.map((l) => (
            <a key={l.key} href={l.href} data-testid={`mobile-nav-${l.key}`} onClick={() => setMobile(false)}
              className="text-zinc-300 hover:text-yellow-400 uppercase tracking-widest font-semibold">
              {l.label}
            </a>
          ))}
          <Link to={user ? "/account" : "/login"} onClick={() => setMobile(false)}
            className="text-yellow-400 uppercase tracking-widest font-semibold flex items-center gap-2">
            <UserIcon className="w-4 h-4" /> {user ? t("nav.account") : t("nav.login")}
          </Link>
          <button onClick={toggleLang} className="text-zinc-300 hover:text-yellow-400 uppercase tracking-widest font-semibold flex items-center gap-2 text-left">
            <Globe className="w-4 h-4" /> {i18n.language === "it" ? "Italiano" : "English"}
          </button>
          {user && (
            <button onClick={() => { logout(); setMobile(false); }}
              className="text-red-400 uppercase tracking-widest font-semibold flex items-center gap-2 text-left">
              <LogOut className="w-4 h-4" /> {t("nav.logout")}
            </button>
          )}
        </div>
      )}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
